import { z } from 'zod';
/**
 * Public agent-release checksums for `/api/v1` (KIR-5145).
 *
 * The Get Started → Install tab shows a SHA-256 under each installer download
 * and, behind "View all checksums", about a year of earlier releases (KIR-4071,
 * KIR-4955). Customers paste those digests into their EDR allow-list. This is
 * the same list for a script: the same files, newest release first, read from
 * the same cached release manifest the app reads.
 *
 * What it deliberately does not carry, and why, is recorded in NAMING.md under
 * "Agent releases (KIR-5145)": no release date (the release source publishes
 * none) and no platform/architecture enum (the source keys digests by file
 * name, which is also what the app shows).
 */
/**
 * The digest algorithm. A one-value enum rather than a bare literal so a second
 * algorithm could later be published as a sibling field without retyping this
 * one — and named, like every enum here, so it renders as a component.
 */
export const v1ChecksumAlgorithmSchema = z.enum(['sha256']).meta({
    id: 'V1ChecksumAlgorithm',
    title: 'Checksum algorithm',
    description: 'The digest algorithm used for every `sha256` value in the response. Always `sha256` in v1.',
});
/** One installer file of one release. */
export const v1AgentInstallerSchema = z
    .object({
    fileName: z
        .string()
        .describe('The installer file name exactly as it is distributed, e.g. `Kirin-3.101.5.pkg` or `Kirin-3.101.5-arm64.exe`. This is the name an EDR reports for the file.'),
    sha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .describe('The SHA-256 digest of the file, as 64 lowercase hexadecimal characters.'),
    downloadUrl: z
        .string()
        .url()
        .describe('Where this exact file is served from. Downloading it and hashing it reproduces `sha256`.'),
})
    .meta({
    id: 'V1AgentInstaller',
    title: 'Agent installer',
    description: 'One distributed Kirin agent installer file and its checksum. IDE extension packages (`.vsix`) are not listed — they install into the editor rather than run as executables an EDR flags.',
});
/** One released agent version and its installers. */
export const v1AgentReleaseSchema = z
    .object({
    version: z.string().describe('The Kirin agent version, e.g. `3.101.5`.'),
    latest: z
        .boolean()
        .describe('Whether this is the version reported in `latestVersion`. At most one release is `true`; none is when the latest version has no published checksums yet.'),
    installers: z
        .array(v1AgentInstallerSchema)
        .describe('Every installer file of this release, ordered by file name.'),
})
    .meta({
    id: 'V1AgentRelease',
    title: 'Agent release',
    description: 'One released Kirin agent version and its installer checksums.',
});
/** `GET /organizations/:organizationId/agent-releases`. */
export const v1AgentReleasesSchema = z
    .object({
    latestVersion: z
        .string()
        .nullable()
        .describe('The latest generally available Kirin agent version — the one the app offers for download. `null` when it cannot be determined right now.'),
    checksumAlgorithm: v1ChecksumAlgorithmSchema,
    releases: z
        .array(v1AgentReleaseSchema)
        .describe('Released versions that have published checksums, newest first, covering about the last year. Empty when no checksums are published yet.'),
})
    .meta({
    id: 'V1AgentReleases',
    title: 'Agent releases',
    description: 'Kirin agent installer checksums for EDR allow-listing: the latest version and about a year of earlier releases, newest first. The same list the app shows under Get Started → Install → "View all checksums".',
    examples: [
        {
            latestVersion: '3.101.5',
            checksumAlgorithm: 'sha256',
            releases: [
                {
                    version: '3.101.5',
                    latest: true,
                    installers: [
                        {
                            fileName: 'Kirin-3.101.5.pkg',
                            sha256: '9f86d081884c7d659a2feaa0c55ad015a3bf4f1b2b0b822cd15d6c15b0f00a08',
                            downloadUrl: 'https://downloads.getkirin.com/kirin-agent/v3.101.5/Kirin-3.101.5.pkg',
                        },
                    ],
                },
            ],
        },
    ],
});
//# sourceMappingURL=agent-releases.js.map